import { Box } from '@mui/material';
import { CalendarChartWidget } from '@/widgets/calendarChart';
import {
  CalendarInsightsDashboardWidget,
  dataConfigValidator as ciDataConfigValidator,
} from '@/widgets/calendarInsights';
import { CohortChartWidget } from '@/widgets/cohortChart';
import {
  CohortMarketingPerformanceDashboardWidget,
  dataConfigValidator as cmpDataConfigValidator,
} from '@/widgets/cohortMarketingPerformance';
import {
  dataConfigValidator as cohortRetentionConfigValidator,
  CohortRetentionWidget,
  DataFetchersProvider as CohortRetentionDataFetchersProvider,
} from '@/widgets/cohortRetention';
import {
  dataConfigValidator as cohortScannerConfigValidator,
  CohortScannerWidget,
  DataFetchersProvider as CohortScannerDataFetchersProvider,
  HighlightMinMaxProvider,
} from '@/widgets/cohortScanner';
import type { DataConfig as CohortScannerDataConfig } from '@/widgets/cohortScanner';
import type { WidgetsConfig } from '@/widgets/dashboards';
import {
  ROIBreakdownWidget,
  dataConfigValidator as roiBreakdownConfigValidator,
} from '@/widgets/roiBreakdown';
import {
  calendarChartDataConfigValidator,
  cohortChartDataConfigValidator,
} from '@/features/chartsView';
import {
  CreativeDetailsDrawer,
  CreativeDrawerContextProvider,
  TableHeaderPropsProvider,
} from '@/features/DataGrid';
import { PresetViewType } from '@/features/savedPresets';
import { ViewType } from '@/entities/session';
import {
  CalendarChartIcon,
  CalendarInsightsIcon,
  CohortMarketingPerformanceIcon,
  CohortChartIcon,
  CohortScannerIcon,
  DataGridApiRefProvider,
  CohortRetentionIcon,
  ROIBreakdownIcon,
} from '@/shared/ui';

const CREATIVES_DRAWER_SX = {
  boxShadow: 'none',
  border: '1px solid #e0e0e0',
  borderRadius: 2,
} as const;

export const WIDGETS_CONFIG: WidgetsConfig = {
  [PresetViewType.cohortMarketingPerformance]: {
    name: 'Cohort Marketing Performance',
    gradientIconUrl: '/images/viewIcons/CohortMarketingPerformanceIcon.svg',
    ViewIcon: CohortMarketingPerformanceIcon,
    validator: (v) => cmpDataConfigValidator.safeParse(v),
    Wrapper: ({ children }) => (
      <DataGridApiRefProvider>
        <TableHeaderPropsProvider
          columnsConfig={{}}
          advancedSortableOptions={[]}
          sorting={[]}
          filters={[]}
          metricsColoring={[]}
          updateSorting={() => {}}
          updateFilters={() => {}}
          updateMetricsColoring={() => {}}
          onDimensionDelete={() => {}}
          onMetricDelete={() => {}}
        >
          <CreativeDrawerContextProvider>
            <Box
              sx={{
                display: 'flex',
                flex: 1,
                overflow: 'hidden',
                height: '100%',
              }}
            >
              {children}
              <CreativeDetailsDrawer sx={CREATIVES_DRAWER_SX} />
            </Box>
          </CreativeDrawerContextProvider>
        </TableHeaderPropsProvider>
      </DataGridApiRefProvider>
    ),
    Component: CohortMarketingPerformanceDashboardWidget,
    viewType: ViewType.cohortMarketingPerformance,
  },
  [PresetViewType.calendarInsights]: {
    name: 'Calendar Insights',
    gradientIconUrl: '/images/viewIcons/CalendarInsightsIcon.svg',
    ViewIcon: CalendarInsightsIcon,
    validator: (v) => ciDataConfigValidator.safeParse(v),
    Wrapper: ({ children }) => (
      <DataGridApiRefProvider>
        <TableHeaderPropsProvider
          columnsConfig={{}}
          advancedSortableOptions={[]}
          sorting={[]}
          filters={[]}
          metricsColoring={[]}
          updateSorting={() => {}}
          updateFilters={() => {}}
          updateMetricsColoring={() => {}}
          onDimensionDelete={() => {}}
          onMetricDelete={() => {}}
        >
          <CreativeDrawerContextProvider>
            <Box
              sx={{
                display: 'flex',
                flex: 1,
                overflow: 'hidden',
                height: '100%',
              }}
            >
              {children}
              <CreativeDetailsDrawer sx={CREATIVES_DRAWER_SX} />
            </Box>
          </CreativeDrawerContextProvider>
        </TableHeaderPropsProvider>
      </DataGridApiRefProvider>
    ),
    Component: CalendarInsightsDashboardWidget,
    viewType: ViewType.calendarInsights,
  },
  [PresetViewType.cohortChart]: {
    name: 'Cohort Chart',
    gradientIconUrl: '/images/viewIcons/CohortChartIcon.svg',
    ViewIcon: CohortChartIcon,
    validator: (v) => cohortChartDataConfigValidator.safeParse(v),
    Wrapper: ({ children }) => children,
    Component: CohortChartWidget,
    viewType: ViewType.cohortChart,
  },
  [PresetViewType.calendarChart]: {
    name: 'Calendar Chart',
    gradientIconUrl: '/images/viewIcons/CalendarChartIcon.svg',
    ViewIcon: CalendarChartIcon,
    validator: (v) => calendarChartDataConfigValidator.safeParse(v),
    Wrapper: ({ children }) => children,
    Component: CalendarChartWidget,
    viewType: ViewType.calendarChart,
  },
  [PresetViewType.cohortScanner]: {
    name: 'Cohort Scanner',
    gradientIconUrl: '/images/viewIcons/CohortScannerIcon.svg',
    ViewIcon: CohortScannerIcon,
    validator: (v) => cohortScannerConfigValidator.safeParse(v),
    Wrapper: ({ preset, children }) => (
      <DataGridApiRefProvider>
        <CohortScannerDataFetchersProvider preset={preset}>
          <HighlightMinMaxProvider
            highlight={(preset as CohortScannerDataConfig).highlight}
          >
            {children}
          </HighlightMinMaxProvider>
        </CohortScannerDataFetchersProvider>
      </DataGridApiRefProvider>
    ),
    Component: CohortScannerWidget,
    viewType: ViewType.cohortScanner,
  },
  [PresetViewType.cohortRetention]: {
    name: 'Cohort Retention',
    gradientIconUrl: '/images/viewIcons/CohortRetentionIcon.svg',
    ViewIcon: CohortRetentionIcon,
    validator: (v) => cohortRetentionConfigValidator.safeParse(v),
    Wrapper: ({ preset, children }) => (
      <DataGridApiRefProvider>
        <CohortRetentionDataFetchersProvider preset={preset}>
          {children}
        </CohortRetentionDataFetchersProvider>
      </DataGridApiRefProvider>
    ),
    Component: CohortRetentionWidget,
    viewType: ViewType.cohortRetention,
  },
  [PresetViewType.roiBreakdown]: {
    name: 'ROI Breakdown',
    gradientIconUrl: '/images/viewIcons/ROIBreakdownIcon.svg',
    ViewIcon: ROIBreakdownIcon,
    validator: (v) => roiBreakdownConfigValidator.safeParse(v),
    Wrapper: ({ children }) => children,
    Component: ROIBreakdownWidget,
    viewType: ViewType.roiBreakdown,
  },
};
